/**
 * Shared notification feed list — rendered both in the top-bar bell panel
 * (variant="panel") and on the full-page feed (variant="page"). Rows deep-link
 * to their decision surface (attention card / inbox thread) — never a dead end.
 *
 * Spec: 08-ui-spec.md §NTF Page 3.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  EmptyState,
  Skeleton,
} from '@teivor/shared-ui';

import { useMarkAllRead, useMarkRead, useNotifications } from '../queries';
import type { NotificationItem } from '../types';
import { relativeTime, fullTimestamp } from '../format';

import { kindIcon, kindLabelKey, humanizeKind, KIND_FILTERS } from './kinds';

interface NotificationListProps {
  variant: 'panel' | 'page';
  /** Called after a row navigates (lets the bell panel close itself). */
  onNavigate?: () => void;
}

export function NotificationList({ variant, onNavigate }: NotificationListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filterId, setFilterId] = React.useState('all');
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const kind = KIND_FILTERS.find((f) => f.id === filterId)?.kind;
  const pageSize = variant === 'panel' ? 15 : 30;

  const { data, isLoading, isError, refetch } = useNotifications({
    kind,
    page: 1,
    pageSize,
  });

  const items = data?.items ?? [];
  const now = Date.now();

  const handleRow = (n: NotificationItem) => {
    if (n.status !== 'read') {
      markRead.mutate({ id: n.id, version: n.version });
    }
    onNavigate?.();
    if (n.deepLinkTarget) navigate(n.deepLinkTarget);
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b border-[var(--tvl-hairline)] px-3 py-2">
        <span className="text-sm font-semibold text-[var(--tvl-ink)]">
          {t('ntf.feed.title')}
        </span>
        <button
          type="button"
          onClick={() => markAllRead.mutate()}
          disabled={markAllRead.isPending || items.length === 0}
          className="rounded-[var(--tvl-r1)] px-1.5 py-0.5 text-xs text-[var(--tvl-link)] outline-none hover:underline focus-visible:outline-2 focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-40"
        >
          {t('ntf.feed.mark_all_read')}
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--tvl-hairline)] px-3 py-2">
        {KIND_FILTERS.map((f) => {
          const active = f.id === filterId;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilterId(f.id)}
              aria-pressed={active}
              className={[
                'inline-flex items-center rounded-[var(--tvl-r2)] px-2.5 py-1 text-xs font-medium',
                'outline-none focus-visible:outline-2 focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-2',
                active
                  ? 'bg-[var(--tvl-accent)] text-[var(--tvl-on-accent)]'
                  : 'border border-[var(--tvl-hairline)] bg-[var(--tvl-raised)] text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
              ].join(' ')}
            >
              {t(f.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div className={variant === 'panel' ? 'max-h-[26rem] overflow-y-auto' : ''}>
        {isLoading ? (
          <div className="flex flex-col gap-2 p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} lines={2} />
            ))}
          </div>
        ) : isError ? (
          <EmptyState
            variant="error-loading"
            title={t('ntf.feed.load_error_title')}
            description={t('ntf.feed.load_error_desc')}
            action={
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                {t('ntf.feed.retry')}
              </Button>
            }
          />
        ) : items.length === 0 ? (
          <EmptyState variant="empty" title={t('ntf.feed.empty')} />
        ) : (
          <ul className="flex flex-col p-0">
            {items.map((n) => {
              const Icon = kindIcon(n.notificationKind);
              const labelKey = kindLabelKey(n.notificationKind);
              const unread = n.status !== 'read';
              const isTop = n.priority === 'top';
              return (
                <li key={n.id} className="border-b border-[var(--tvl-hairline)] last:border-b-0">
                  <button
                    type="button"
                    onClick={() => handleRow(n)}
                    className={[
                      'flex w-full items-start gap-3 px-3 py-2.5 text-left',
                      'outline-none transition-colors hover:bg-[var(--tvl-wash-hover)]',
                      'focus-visible:outline-2 focus-visible:outline-[color:var(--tvl-focus)] focus-visible:-outline-offset-2',
                      isTop ? 'bg-[var(--tvl-selected)]' : '',
                    ].join(' ')}
                  >
                    {/* Kind icon */}
                    <span className="mt-0.5 shrink-0 text-[var(--tvl-ink-2)]">
                      <Icon size={18} color="currentColor" aria-hidden />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        {isTop ? (
                          <span
                            className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--tvl-accent)]"
                            aria-hidden
                          />
                        ) : unread ? (
                          <span
                            className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--tvl-ink-3)]"
                            aria-hidden
                          />
                        ) : null}
                        <span
                          className={[
                            'truncate text-sm',
                            unread ? 'font-medium text-[var(--tvl-ink)]' : 'text-[var(--tvl-ink-2)]',
                          ].join(' ')}
                        >
                          {labelKey ? t(labelKey) : humanizeKind(n.notificationKind)}
                        </span>
                      </span>

                      <span className="mt-1 flex flex-wrap items-center gap-1.5">
                        {isTop ? (
                          <span className="inline-flex items-start rounded-[var(--tvl-r2)] bg-[var(--tvl-accent)] px-1.5 py-0.5 text-[10px] font-medium leading-[14px] text-[var(--tvl-on-accent)]">
                            {t('ntf.feed.priority')}
                          </span>
                        ) : null}
                        {n.workspaceId ? (
                          <span className="inline-flex items-start rounded-[var(--tvl-r2)] border border-[var(--tvl-hairline)] bg-[var(--tvl-raised)] px-1.5 py-0.5 text-[10px] leading-[14px] text-[var(--tvl-ink-3)] [font-family:var(--tvl-font-mono)]">
                            {n.workspaceId.slice(0, 8)}
                          </span>
                        ) : (
                          <span className="inline-flex items-start rounded-[var(--tvl-r2)] border border-[var(--tvl-hairline)] bg-[var(--tvl-raised)] px-1.5 py-0.5 text-[10px] leading-[14px] text-[var(--tvl-ink-3)]">
                            {t('ntf.feed.system')}
                          </span>
                        )}
                      </span>
                    </span>

                    {/* Relative time */}
                    <span
                      title={fullTimestamp(n.createdAt)}
                      className="mt-0.5 shrink-0 text-xs tabular-nums text-[var(--tvl-ink-3)] [font-family:var(--tvl-font-mono)]"
                    >
                      {relativeTime(n.createdAt, now)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
